(() => {
  const EXPECTED_PRODUCT_ID = '9922672001367';
  const STORAGE_KEY = 'sp_paid_attribution_v1';
  const PROPERTY_FIRST = 'properties[_sp_paid_first]';
  const PROPERTY_LAST = 'properties[_sp_paid_last]';
  const PROPERTY_TOUCH_ID = 'properties[_sp_touch_id]';
  const PROPERTY_FIRST_HASHES = 'properties[_sp_paid_first_hashes]';
  const PROPERTY_LAST_HASHES = 'properties[_sp_paid_last_hashes]';
  const CART_REVOCATION_ATTRIBUTE = '__sp_paid_revoked_at_v1';
  const PRODUCT_FORM_SELECTOR = 'form[is="product-form"], form.sticky-atc-bar__form';
  const PROPERTY_SELECTOR = 'input[data-sp-paid-attribution="true"]';
  // Een adklik blijft 30 dagen bruikbaar. Dat is lang genoeg voor een
  // overwogen aankoop, maar voorkomt dat maandenoude advertenties een order
  // blijven opeisen.
  const PAID_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  const MAX_DESCRIPTOR_LENGTH = 640;
  const CAPTURE_FAIL_OPEN_MS = 500;
  const MAX_CLOCK_SKEW_MS = 5 * 60 * 1000;
  const MAX_REVOCATION_ATTEMPTS = 3;
  const PAID_ID_NAMES = ['gclid', 'gbraid', 'wbraid', 'oppref'];
  const CLICK_ID_TYPES = new Set([
    'gclid',
    'gbraid',
    'wbraid',
    'gclid+gbraid',
    'gclid+wbraid',
    'gbraid+wbraid',
    'gclid+gbraid+wbraid',
    'oppref',
    'utm_only',
  ]);
  const SOURCE_TYPES = new Set(['google_ads', 'chatgpt_ads']);
  const PAID_MEDIA = /^(?:cpc|ppc|paid|paidsearch|paid_search|search_paid)$/i;
  const GOOGLE_SOURCES = /^(?:google|googleads|google_ads|adwords)$/i;
  const CHATGPT_SOURCES = /^(?:chatgpt|openai|chatgpt_ads|openai_ads)$/i;
  const EMAIL_LIKE = /(?:^|[^a-z0-9])[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)+(?:$|[^a-z0-9])/i;
  const PHONE_LIKE = /(?:^|\D)(?:\+?\d[\d .()/-]{7,}\d)(?:$|\D)/;

  let cartRevokedAt = 0;
  let cartRevocationInvalid = false;
  let cartRevocationReady = false;
  let initialCartInspectionPromise = null;
  let revocationInFlight = null;
  let pendingRevocationAt = 0;
  let revocationAttemptCount = 0;
  let lastConsentAllowed = null;

  const normalizeId = (value) => {
    const match = String(value || '').match(/(\d+)(?:\D*)$/);
    return match ? match[1] : '';
  };

  const cleanCampaignValue = (value, maximum, encodedMaximum = 96) => {
    const characters = Array.from(String(value || '').replace(/\s+/g, ' ').trim()).slice(0, maximum);
    const cleaned = characters.join('');
    if (!cleaned || EMAIL_LIKE.test(cleaned) || PHONE_LIKE.test(cleaned)) return '';
    while (characters.length && encodeURIComponent(characters.join('')).length > encodedMaximum) {
      characters.pop();
    }
    return encodeURIComponent(characters.join(''));
  };

  const cleanClickId = (value) => {
    const cleaned = String(value || '').trim();
    if (!cleaned || cleaned.length > 220) return '';
    // Click references are treated as opaque values. RFC 3986 unreserved
    // characters cover the known Google/OpenAI formats; '=' is allowed only
    // for possible base64 padding before the value is immediately hashed.
    return /^[A-Za-z0-9._~=-]+$/.test(cleaned) ? cleaned : '';
  };

  const parseRevocationTimestamp = (value, now = Date.now()) => {
    const match = String(value || '').trim().match(/^v1\|(\d{1,16})$/);
    if (!match) return 0;
    const timestamp = Number(match[1]);
    return Number.isSafeInteger(timestamp) &&
      timestamp > 0 &&
      timestamp <= now + MAX_CLOCK_SKEW_MS
      ? timestamp
      : 0;
  };

  const serializeRevocationTimestamp = (timestamp) =>
    `v1|${Math.floor(Number(timestamp))}`;

  const cartEndpoint = (pathname) => {
    const root = String(window.Shopify?.routes?.root || '/');
    return `${root.endsWith('/') ? root : `${root}/`}${pathname}`;
  };

  const fetchCart = async () => {
    if (typeof window.fetch !== 'function') throw new Error('Cart API unavailable');
    const response = await window.fetch(cartEndpoint('cart.js'), {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response?.ok) throw new Error('Cart read failed');
    const cart = await response.json();
    const attributes = cart?.attributes || {};
    const hasRevocationAttribute = Object.prototype.hasOwnProperty.call(
      attributes,
      CART_REVOCATION_ATTRIBUTE
    );
    const revokedAt = parseRevocationTimestamp(attributes[CART_REVOCATION_ATTRIBUTE]);
    cartRevocationInvalid = hasRevocationAttribute && !revokedAt;
    cartRevokedAt = Math.max(cartRevokedAt, revokedAt);
    return cart;
  };

  const ensureCartRevocationLoaded = () => {
    if (cartRevocationReady) return Promise.resolve();
    if (!initialCartInspectionPromise) {
      initialCartInspectionPromise = fetchCart()
        .catch(() => null)
        .finally(() => {
          cartRevocationReady = true;
        });
    }
    return initialCartInspectionPromise;
  };

  const cartHasPaidProperties = (cart) =>
    Array.isArray(cart?.items) && cart.items.some((item) =>
      Object.keys(item?.properties || {}).some((key) => /^_sp_paid_/.test(key))
    );

  const writeCartRevocation = (requestedAt) => {
    if (!requestedAt || revocationAttemptCount >= MAX_REVOCATION_ATTEMPTS) {
      return Promise.resolve(false);
    }
    if (revocationInFlight) return revocationInFlight;
    revocationAttemptCount += 1;
    revocationInFlight = (async () => {
      const cart = await fetchCart();
      const existing = parseRevocationTimestamp(cart?.attributes?.[CART_REVOCATION_ATTRIBUTE]);
      if (existing >= requestedAt || !cartHasPaidProperties(cart)) return true;
      const response = await window.fetch(cartEndpoint('cart/update.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            [CART_REVOCATION_ATTRIBUTE]: serializeRevocationTimestamp(requestedAt),
          },
        }),
      });
      if (!response?.ok) throw new Error('Cart revocation write failed');
      cartRevocationInvalid = false;
      cartRevokedAt = Math.max(cartRevokedAt, requestedAt);
      return true;
    })()
      .catch(() => false)
      .then((completed) => {
        if (completed && pendingRevocationAt === requestedAt) pendingRevocationAt = 0;
        return completed;
      })
      .finally(() => {
        revocationInFlight = null;
        if (pendingRevocationAt && pendingRevocationAt !== requestedAt) {
          void writeCartRevocation(pendingRevocationAt);
        }
      });
    return revocationInFlight;
  };

  const beginCartRevocation = (timestamp = Date.now()) => {
    const requestedAt = Math.floor(Number(timestamp));
    if (!Number.isSafeInteger(requestedAt) || requestedAt <= 0) return;
    cartRevokedAt = Math.max(cartRevokedAt, requestedAt);
    cartRevocationReady = true;
    pendingRevocationAt = requestedAt;
    revocationAttemptCount = 0;
    void writeCartRevocation(requestedAt);
  };

  const retryCartRevocation = () => {
    if (!pendingRevocationAt || revocationAttemptCount >= MAX_REVOCATION_ATTEMPTS) return;
    void writeCartRevocation(pendingRevocationAt);
  };

  const analyticsAllowed = () => {
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
    } catch (_error) {
      return false;
    }
  };

  const marketingAllowed = () => {
    try {
      return window.Shopify?.customerPrivacy?.marketingAllowed?.() === true;
    } catch (_error) {
      return false;
    }
  };

  const privacyApiReady = () =>
    typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function' &&
    typeof window.Shopify?.customerPrivacy?.marketingAllowed === 'function';

  const paidProcessingAllowed = () =>
    privacyApiReady() && analyticsAllowed() && marketingAllowed();

  const randomTouchId = () => {
    try {
      if (typeof window.crypto?.randomUUID === 'function') {
        const value = window.crypto.randomUUID().replace(/-/g, '').toLowerCase();
        return /^[a-f0-9]{32}$/.test(value) ? value : '';
      }
      if (typeof window.crypto?.getRandomValues !== 'function') return '';
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      return [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('');
    } catch (_error) {
      return '';
    }
  };

  const validTouchId = (value) => /^[a-f0-9]{32}$/.test(String(value || ''));

  const clickIdType = (identifiers, validator) => {
    const present = PAID_ID_NAMES.filter((name) => validator(identifiers?.[name]));
    return present.length ? present.join('+') : 'utm_only';
  };

  const cleanClickHash = (value) => {
    const cleaned = String(value || '').trim().toLowerCase();
    return /^[a-f0-9]{64}$/.test(cleaned) ? cleaned : '';
  };

  const hashClickId = async (value) => {
    const clickId = cleanClickId(value);
    if (!clickId || typeof window.crypto?.subtle?.digest !== 'function') return '';
    try {
      const bytes = new TextEncoder().encode(clickId);
      const digest = await window.crypto.subtle.digest('SHA-256', bytes);
      return [...new Uint8Array(digest)]
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');
    } catch (_error) {
      return '';
    }
  };

  const validTouch = (touch, now = Date.now()) => {
    const capturedAt = Number(touch?.captured_at);
    const type = String(touch?.click_id_type || '');
    const hashes = touch?.click_hashes || {};
    const present = PAID_ID_NAMES.filter((name) => cleanClickHash(hashes[name]));
    if (
      Number(touch?.v) !== 1 ||
      !validTouchId(touch?.touch_id) ||
      !SOURCE_TYPES.has(touch?.source_type) ||
      !CLICK_ID_TYPES.has(type) ||
      type !== (present.length ? present.join('+') : 'utm_only') ||
      !Number.isFinite(capturedAt) ||
      capturedAt <= 0 ||
      cartRevocationInvalid ||
      capturedAt <= cartRevokedAt ||
      capturedAt > now + MAX_CLOCK_SKEW_MS ||
      capturedAt + PAID_TTL_MS <= now
    ) return false;
    const source = decodeURIComponent(String(touch?.utm_source || ''));
    const medium = decodeURIComponent(String(touch?.utm_medium || ''));
    const sourceMatches = touch.source_type === 'google_ads'
      ? GOOGLE_SOURCES.test(source)
      : CHATGPT_SOURCES.test(source);
    if (!sourceMatches || !PAID_MEDIA.test(medium)) return false;
    if (touch.source_type === 'chatgpt_ads') return type === 'oppref' || type === 'utm_only';
    return type !== 'oppref' && !type.includes('oppref');
  };

  const normalizedStoredContext = (candidate) => {
    const now = Date.now();
    if (Number(candidate?.v) !== 1 || !validTouch(candidate?.last, now)) return null;
    const first = validTouch(candidate?.first, now) ? candidate.first : candidate.last;
    return { v: 1, first, last: candidate.last };
  };

  const readContext = () => {
    try {
      if (!privacyApiReady()) return null;
      if (!cartRevocationReady) return null;
      if (!paidProcessingAllowed()) {
        window.localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const context = normalizedStoredContext(JSON.parse(raw));
      if (!context) {
        window.localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return context;
    } catch (_error) {
      return null;
    }
  };

  const touchFromUrl = async (href) => {
    try {
      const url = new URL(href);
      const clickIds = Object.fromEntries(
        PAID_ID_NAMES.map((name) => [
          name,
          cleanClickId(
            name === 'oppref'
              ? url.searchParams.get('oppref') || url.searchParams.get('sp_oppref')
              : url.searchParams.get(name)
          ),
        ])
      );
      const suppliedSource = cleanCampaignValue(url.searchParams.get('utm_source'), 40, 60);
      const suppliedMedium = cleanCampaignValue(url.searchParams.get('utm_medium'), 30, 40);
      const type = clickIdType(clickIds, cleanClickId);
      const isGooglePaidUtm =
        GOOGLE_SOURCES.test(decodeURIComponent(suppliedSource)) &&
        PAID_MEDIA.test(decodeURIComponent(suppliedMedium));
      const isChatGptPaidUtm =
        CHATGPT_SOURCES.test(decodeURIComponent(suppliedSource)) &&
        PAID_MEDIA.test(decodeURIComponent(suppliedMedium));
      const hasGoogleId = ['gclid', 'gbraid', 'wbraid'].some((name) => clickIds[name]);
      const hasOppref = Boolean(clickIds.oppref);
      if (hasGoogleId && hasOppref) return null;
      const sourceType = hasOppref || (!hasGoogleId && isChatGptPaidUtm)
        ? 'chatgpt_ads'
        : 'google_ads';
      if (type === 'utm_only' && !isGooglePaidUtm && !isChatGptPaidUtm) return null;

      const touchId = randomTouchId();
      if (!touchId) return null;
      const clickHashes = Object.fromEntries(
        await Promise.all(
          PAID_ID_NAMES.map(async (name) => [name, await hashClickId(clickIds[name])])
        )
      );
      if (type !== clickIdType(clickHashes, cleanClickHash)) return null;
      const touch = {
        v: 1,
        touch_id: touchId,
        source_type: sourceType,
        click_id_type: type,
        click_hashes: clickHashes,
        // Source en medium zijn bewust canoniek. De advertentie-ID's en
        // campagnevelden blijven apart beschikbaar voor analyse.
        utm_source: sourceType === 'chatgpt_ads' ? 'chatgpt' : 'google',
        utm_medium: 'cpc',
        utm_campaign: cleanCampaignValue(url.searchParams.get('utm_campaign'), 80, 96),
        utm_content: cleanCampaignValue(url.searchParams.get('utm_content'), 80, 96),
        utm_id: cleanCampaignValue(url.searchParams.get('utm_id'), 80, 96),
        ad_group_id: cleanCampaignValue(
          url.searchParams.get('sp_ad_group_id') || url.searchParams.get('ad_group_id'),
          80,
          96
        ),
        ad_account_id: cleanCampaignValue(
          url.searchParams.get('sp_ad_account_id') || url.searchParams.get('ad_account_id'),
          80,
          96
        ),
        captured_at: Date.now(),
        landing_path: cleanCampaignValue(url.pathname, 120, 180),
      };
      return validTouch(touch) ? touch : null;
    } catch (_error) {
      return null;
    }
  };

  const writeTouch = (touch) => {
    if (!paidProcessingAllowed() || !validTouch(touch)) return null;
    try {
      const current = readContext();
      const currentSignature = current?.last
        ? JSON.stringify([
            current.last.click_id_type,
            current.last.source_type,
            current.last.click_hashes,
            current.last.utm_source,
            current.last.utm_medium,
            current.last.utm_campaign,
            current.last.utm_content,
            current.last.utm_id,
            current.last.ad_group_id,
            current.last.ad_account_id,
          ])
        : '';
      const nextSignature = JSON.stringify([
        touch.click_id_type,
        touch.source_type,
        touch.click_hashes,
        touch.utm_source,
        touch.utm_medium,
        touch.utm_campaign,
        touch.utm_content,
        touch.utm_id,
        touch.ad_group_id,
        touch.ad_account_id,
      ]);
      const isSameClick = currentSignature && currentSignature === nextSignature;
      if (
        isSameClick &&
        (touch.click_id_type !== 'utm_only' ||
          touch.captured_at - current.last.captured_at <= 30 * 60 * 1000)
      ) return current;

      const context = {
        v: 1,
        first: current?.first || touch,
        last: touch,
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(context));
      return context;
    } catch (_error) {
      return null;
    }
  };

  const serializeTouch = (touch) => {
    const value = [
      'v1',
      Math.floor(Number(touch.captured_at) / 1000),
      touch.touch_id,
      touch.source_type,
      touch.click_id_type,
      touch.utm_source || (touch.source_type === 'chatgpt_ads' ? 'chatgpt' : 'google'),
      touch.utm_medium || 'cpc',
      touch.utm_campaign || '',
      touch.utm_content || '',
      touch.utm_id || '',
      touch.ad_group_id || '',
      touch.ad_account_id || '',
    ].join('|');
    return value.length <= MAX_DESCRIPTOR_LENGTH ? value : '';
  };

  const serializeHashes = (touch) => {
    const entries = PAID_ID_NAMES
      .filter((name) => cleanClickHash(touch?.click_hashes?.[name]))
      .map((name) => `${name}:${cleanClickHash(touch.click_hashes[name])}`);
    return ['v1', ...(entries.length ? entries : ['none'])].join('|');
  };

  const targetForms = (root) => {
    const forms = new Set();
    if (root?.matches?.(PRODUCT_FORM_SELECTOR)) forms.add(root);
    const enclosing = root?.closest?.(PRODUCT_FORM_SELECTOR);
    if (enclosing) forms.add(enclosing);
    root?.querySelectorAll?.(PRODUCT_FORM_SELECTOR).forEach((form) => forms.add(form));
    return [...forms];
  };

  const isSidePillowForm = (form) => {
    const productId = normalizeId(
      form?.dataset?.productId ||
      form?.querySelector('input[name="product-id"]')?.value ||
      form?.closest('[data-product-id]')?.dataset?.productId
    );
    return productId === EXPECTED_PRODUCT_ID;
  };

  const removeProperties = (root = document) => {
    const inputs = new Set();
    if (root?.matches?.(PROPERTY_SELECTOR)) inputs.add(root);
    root?.closest?.(PRODUCT_FORM_SELECTOR)?.querySelectorAll?.(PROPERTY_SELECTOR)
      .forEach((input) => inputs.add(input));
    root?.querySelectorAll?.(PROPERTY_SELECTOR).forEach((input) => inputs.add(input));
    inputs.forEach((input) => input.remove());
  };

  const setProperty = (form, name, value) => {
    let input = form.querySelector(`input[name="${name}"]`);
    if (!value) {
      input?.remove();
      return;
    }
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.dataset.spPaidAttribution = 'true';
      form.appendChild(input);
    }
    input.value = value;
    input.setAttribute('value', value);
  };

  let lastPublishedCapture = '';
  let lastPublishedAttachment = '';

  const publish = async (eventName, payload, retries = 0) => {
    if (!paidProcessingAllowed()) return false;
    const shopifyAnalytics = window.Shopify?.analytics;
    if (!shopifyAnalytics || typeof shopifyAnalytics.publish !== 'function') {
      if (retries < 20) window.setTimeout(() => publish(eventName, payload, retries + 1), 250);
      return false;
    }
    try {
      const accepted = await Promise.resolve(shopifyAnalytics.publish(eventName, payload));
      return accepted !== false;
    } catch (_error) {
      return false;
    }
  };

  const diagnosticPayload = (context) => ({
    schema_version: 1,
    product_id: EXPECTED_PRODUCT_ID,
    source_type: context.last.source_type,
    click_id_type: context.last.click_id_type,
    first_last_same: context.first.touch_id === context.last.touch_id,
    campaign_present: Boolean(context.last.utm_campaign),
  });

  const publishCapture = (context) => {
    const touchId = context?.last?.touch_id || '';
    if (!touchId || touchId === lastPublishedCapture) return;
    lastPublishedCapture = touchId;
    void publish('sidepillow:paid_context_captured', diagnosticPayload(context));
  };

  const injectProperties = (root = document) => {
    const context = readContext();
    if (!context) {
      removeProperties(root);
      return null;
    }
    let attached = false;
    targetForms(root).forEach((form) => {
      if (!isSidePillowForm(form)) {
        form.querySelectorAll(PROPERTY_SELECTOR).forEach((input) => input.remove());
        return;
      }
      const idInput = form.querySelector('input[name="id"], select[name="id"]');
      if (!idInput) return;
      const serializedFirst = serializeTouch(context.first);
      const serializedLast = serializeTouch(context.last);
      if (!serializedFirst || !serializedLast) {
        form.querySelectorAll(PROPERTY_SELECTOR).forEach((input) => input.remove());
        return;
      }
      setProperty(form, PROPERTY_FIRST, serializedFirst);
      setProperty(form, PROPERTY_LAST, serializedLast);
      setProperty(form, PROPERTY_TOUCH_ID, context.last.touch_id);
      setProperty(form, PROPERTY_FIRST_HASHES, serializeHashes(context.first));
      setProperty(form, PROPERTY_LAST_HASHES, serializeHashes(context.last));
      attached = true;
    });
    if (attached && context.last.touch_id !== lastPublishedAttachment) {
      lastPublishedAttachment = context.last.touch_id;
      void publish('sidepillow:paid_context_attached', diagnosticPayload(context));
    }
    return context;
  };

  // Alleen de landing van deze paginalaad mag worden vastgelegd. Als de eerste
  // privacy-uitkomst denied is, wordt die URL definitief weggegooid en nooit
  // na een latere opt-in teruggevuld.
  let pendingHref = window.location.href;
  let initialPrivacyResolutionPending = true;
  let capturePromise = null;

  const captureCurrentLanding = async () => {
    await ensureCartRevocationLoaded();
    if (!paidProcessingAllowed()) return null;
    const touch = await touchFromUrl(pendingHref || window.location.href);
    pendingHref = '';
    if (!paidProcessingAllowed()) return null;
    if (!touch) return readContext();
    const context = writeTouch(touch);
    if (context) publishCapture(context);
    return context;
  };

  const captureCurrentLandingOnce = () => {
    if (capturePromise) return capturePromise;
    capturePromise = captureCurrentLanding().finally(() => {
      capturePromise = null;
    });
    return capturePromise;
  };

  const waitForCaptureOrFailOpen = (pendingCapture) => new Promise((resolve) => {
    let settled = false;
    let timeoutId = 0;
    const finish = (captured) => {
      if (settled) return;
      settled = true;
      if (timeoutId) window.clearTimeout?.(timeoutId);
      resolve(captured);
    };
    timeoutId = window.setTimeout(() => finish(false), CAPTURE_FAIL_OPEN_MS);
    Promise.resolve(pendingCapture).then(
      () => finish(true),
      () => finish(false)
    );
  });

  const syncConsent = async () => {
    if (!privacyApiReady()) return;
    const mayCaptureThisLanding = initialPrivacyResolutionPending;
    initialPrivacyResolutionPending = false;
    if (!paidProcessingAllowed()) {
      if (lastConsentAllowed !== false) beginCartRevocation(Date.now());
      lastConsentAllowed = false;
      pendingHref = '';
      lastPublishedCapture = '';
      lastPublishedAttachment = '';
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch (_error) {
        // Consent intrekken mag de storefront nooit blokkeren.
      }
      removeProperties();
      return;
    }
    lastConsentAllowed = true;
    if (mayCaptureThisLanding) await captureCurrentLandingOnce();
    if (!paidProcessingAllowed()) return;
    injectProperties();
  };

  if (privacyApiReady()) void syncConsent();

  document.addEventListener('visitorConsentCollected', () => void syncConsent());
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', () => void syncConsent());
  window.addEventListener?.('online', retryCartRevocation);
  window.addEventListener?.('pageshow', retryCartRevocation);

  const waitingForms = new WeakSet();
  const resumedForms = new WeakSet();
  const waitingButtons = new WeakSet();
  const resumedButtons = new WeakSet();

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form?.matches?.(PRODUCT_FORM_SELECTOR) || !isSidePillowForm(form)) return;
      if (resumedForms.has(form)) {
        resumedForms.delete(form);
        injectProperties(form);
        return;
      }
      const pendingCapture = capturePromise;
      if (!pendingCapture) {
        injectProperties(form);
        return;
      }

      // SHA-256 draait lokaal en duurt normaal slechts milliseconden. Houd een
      // extreem snelle submit heel even vast, zodat de orderproperty niet door
      // een race tussen de klik en Web Crypto verloren gaat.
      event.preventDefault?.();
      event.stopImmediatePropagation?.();
      if (waitingForms.has(form)) return;
      waitingForms.add(form);
      const submitter = event.submitter;
      void waitForCaptureOrFailOpen(pendingCapture).then(() => {
        waitingForms.delete(form);
        injectProperties(form);
        resumedForms.add(form);
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(submitter?.form === form ? submitter : undefined);
        } else if (submitter && typeof submitter.click === 'function') {
          resumedButtons.add(submitter);
          submitter.click();
        } else if (typeof form.submit === 'function') {
          form.submit();
        }
      });
    },
    { capture: true }
  );

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('form[is="product-form"] [name="add"], .shopify-payment-button__button');
      const form = button?.closest?.('form');
      if (!form || !isSidePillowForm(form)) return;
      if (resumedButtons.has(button)) {
        resumedButtons.delete(button);
        injectProperties(form);
        return;
      }
      const pendingCapture = capturePromise;
      if (pendingCapture) {
        event.preventDefault?.();
        event.stopImmediatePropagation?.();
        if (waitingButtons.has(button)) return;
        waitingButtons.add(button);
        void waitForCaptureOrFailOpen(pendingCapture).then(() => {
          waitingButtons.delete(button);
          injectProperties(form);
          resumedButtons.add(button);
          if (button.matches?.('[name="add"]')) resumedForms.add(form);
          button.click();
        });
        return;
      }
      injectProperties(form);
    },
    { capture: true }
  );

  document.addEventListener('product-ajax:added', (event) => {
    const product = event.detail?.product;
    const items = Array.isArray(product?.items) && product.items.length ? product.items : [product];
    const productId = normalizeId(product?.product_id || items[0]?.product_id);
    if (productId !== EXPECTED_PRODUCT_ID) return;
    const context = readContext();
    if (!context) return;
    const variantIds = [
      ...new Set(
        items
          .map((item) => normalizeId(item?.variant_id || item?.id))
          .filter(Boolean)
      ),
    ];
    const quantity = items.reduce(
      (total, item) => total + Math.max(1, Number(item?.quantity) || 1),
      0
    );
    void publish('sidepillow:paid_cart_success', {
      ...diagnosticPayload(context),
      ...(variantIds.length === 1 ? { variant_id: variantIds[0] } : {}),
      variant_ids: variantIds,
      quantity: quantity || Math.max(1, Number(product?.quantity) || 1),
    });
  });

  const mutationObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) injectProperties(node);
      });
    });
  });
  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', () => injectProperties());

  injectProperties();
})();
